import { QueryKey } from 'react-query';
import { RoutePath } from 'Iaso/constants/routes';
import { MenuItem } from 'Iaso/domains/app/types';
import { User } from 'Iaso/utils/usersUtils';

/**
 * check if user has the permission
 *
 * @param {String, Array} permission
 * @param {Object} user
 * @return {Boolean}
 */
export const userHasPermission = (permission: string, user: User): boolean => {
    if (!user) {
        return false;
    }
    if (!user.permissions || !Array.isArray(user.permissions)) return false;
    if (user.is_superuser || user.permissions.find(p => p === permission)) {
        return true;
    }
    return false;
};

/**
 * check if user has one of the permission
 *
 * @param {Array} permissions
 * @param {Object} user
 * @return {Boolean}
 */
export const userHasOneOfPermissions = (
    permissions: string[] = [],
    user: User,
): boolean => {
    if (!user) {
        return false;
    }
    if (user.is_superuser) return true;
    let isAuthorised = false;
    permissions.forEach(p => {
        if (!!p && userHasPermission(p, user)) {
            isAuthorised = true;
        }
    });
    return isAuthorised;
};
/**
 * Check if user has all the specified permissions.
 *
 * @param {Array} permissions - Array of permissions to check.
 * @param {Object} user - User object to check permissions against.
 * @return {Boolean} - Returns true if user has all the permissions, otherwise false.
 */
export const userHasAllPermissions = (
    permissions: string[],
    user: User,
): boolean => {
    if (!user || !Array.isArray(permissions) || permissions.length === 0) {
        return false;
    }
    return permissions.every(permission => userHasPermission(permission, user));
};

/**
 * list all submenu permission
 *
 * @param {Object} menuItem
 * @return {Array}
 */
export const listMenuPermission = (
    menuItem: MenuItem,
    permissions: string[] = [],
): string[] => {
    let permissionsTemp = [...permissions];
    if (menuItem) {
        if (
            menuItem.permissions &&
            menuItem.permissions.length > 0 &&
            !permissionsTemp.find(
                p => menuItem.permissions?.includes(p) ?? false,
            ) // Avoid duplicate permission
        ) {
            permissionsTemp = [...permissionsTemp, ...menuItem.permissions];
        }
        if (menuItem.subMenu) {
            menuItem.subMenu.forEach(subMenuItem => {
                const subPerms = listMenuPermission(
                    subMenuItem,
                    permissionsTemp,
                ).filter(sp => !permissionsTemp.includes(sp)); // Avoid duplicate permission
                permissionsTemp = [...permissionsTemp, ...subPerms];
            });
        }
    }
    return permissionsTemp;
};

/**
 * get the first permission of an user, ignoring root url permission
 *
 * @param {String[]} rootPermissions
 * @param {Object} user
 * @return {String}
 */
export const getFirstAllowedUrl = (
    rootPermissions: string[],
    userPermissions: string[],
    routes: RoutePath[],
): string | undefined => {
    const untestedPermissions = [...userPermissions];
    let newRoot: string | undefined;
    userPermissions.forEach((p, i) => {
        if (!newRoot && !rootPermissions.includes(p)) {
            newRoot = p;
            untestedPermissions.splice(i, 1);
        }
    });
    const newPath = routes
        .filter(route => route.baseUrl !== 'secret')
        .find(p => p.permissions?.some(kp => kp === newRoot));
    if (newPath) {
        return newPath.baseUrl;
    }
    if (untestedPermissions.length === 0) return undefined;
    return getFirstAllowedUrl(rootPermissions, untestedPermissions, routes);
};

/**
 * Check if user account has access to specified module.
 *
 * @param {String} module
 * @param {Object} user
 * @return {Boolean} - Returns true if user account has specified module, otherwise false.
 */
export const userHasAccessToModule = (module: string, user: User): boolean => {
    if (!user) {
        return false;
    }
    if (user?.account?.modules.includes(module)) {
        return true;
    }

    return false;
};

export const userHasRole = (user: User, userRoleId: number): boolean => {
    return user.is_superuser || user.user_roles.includes(userRoleId);
};
export const userHasOneOfRoles = (
    user: User,
    userRoleIds: number[],
): boolean => {
    return (
        user.is_superuser ||
        user.user_roles.some(role => userRoleIds.includes(role))
    );
};

export type ProfilesDropdownParams = Record<string, string>;
export const getProfilesDropdownQueryKey = (
    params: ProfilesDropdownParams,
    triggerWithEmptyQuery = true,
): QueryKey => [
    'profiles',
    'dropdown',
    new URLSearchParams(params).toString(),
    triggerWithEmptyQuery ? 'triggerWithEmptyQuery' : undefined,
];
